import React, { useState } from "react";
import AvisoSoloLectura from "./AvisoSoloLectura";
import ModalValidarPago from "./ModalValidarPago";
import ReasignarPago from "./ReasignarPago";
import { MENSAJE_SOLO_LECTURA } from "../utilities/permisos";
import { avisosParaMostrar } from "../utilities/avisosCajero";
import { calcularInfoParcialidad, confirmadoPorBanco, validadoPorBanco } from "../utilities/historialParcial";

// "Historial de Pagos": antes eran dos pantallas, "Validar pago" (ordenes pendientes) e "Historial" (pagos hechos).
// Ahora es una sola tabla: primero las ordenes pendientes, cada una con su boton para validarla, y despues los pagos.
// Las canceladas no se muestran. La usan Alumnos, Ex-alumnos y Externos.

// puedeReasignar: cajeros (roles 2 y 3) en el historial de alumnos regulares. Solo se ofrece en pagos que el banco
// no ha confirmado y que no son el cobro del faltante de otra orden; el servidor revisa el resto.
export const sePuedeReasignar = (pago) => pago.estatus === "PAGADO" && !confirmadoPorBanco(pago) && !pago.id_intencion_origen && !pago.exalumno;

const dinero = (n) => `$${parseFloat(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const ddmm = (iso) => `${String(iso).substring(8, 10)}/${String(iso).substring(5, 7)}`;
const conceptosDe = (ref) => (Array.isArray(ref.conceptos) ? ref.conceptos.map((c) => c.descripcion).filter(Boolean).join(", ") : ref.conceptos) || "—";

const insignia = (fondo, color) => ({ backgroundColor: fondo, color, padding: "4px 10px", borderRadius: "20px", fontSize: "0.8rem", fontWeight: "bold", whiteSpace: "nowrap" });
const boton = { fontSize: "0.8rem", padding: "5px 10px", marginTop: "6px", display: "block", width: "100%" };

const VistaHistorialDePagos = ({
  cargando = false,
  pendientes = [],
  pagos = [],
  avisos = [], // lo que cambio en las ordenes del alumno sin el cajero (utilities/avisosCajero.js)
  coberturaBanco = [], // [{ fuente, hasta }]: hasta que dia cargo Financieros cada banco
  puedeValidar = false,
  soloLectura = false, // Administrador: ve los botones de validar, deshabilitados
  tienePinpad = false,
  loadingValidacion = false,
  abrirModalValidacion = () => {},
  abrirModalComplemento = () => {},
  // Modal de validacion (mismos nombres que useValidacionPago)
  mostrarModalValidacion = false,
  refSeleccionada = null,
  cerrarModal = () => {},
  confirmarValidacionPago = () => {},
  bancoSeleccionado = "",
  setBancoSeleccionado = () => {},
  folioBanco = "",
  setFolioBanco = () => {},
  modoModal = "validar",
  cobrarFaltantePinpad = () => {},
  cobrarFaltanteTPV = () => {},
  calcularFaltante = () => 0,
  puedeReasignar = false,
  onReasignado = () => {},
}) => {
  const [reasignando, setReasignando] = useState(null);
  const infoParcialidad = calcularInfoParcialidad(pagos);
  const textosAvisos = avisosParaMostrar(avisos);
  const ventanilla = coberturaBanco.filter((c) => c.fuente === "BANORTE" || c.fuente === "BBVA");
  // "Solicitante": nombre de la persona en Ex-alumnos y Externos (columna exalumno); los alumnos ya se identifican arriba
  const haySolicitante = [...pendientes, ...pagos].some((p) => p.exalumno);
  const verBotones = puedeValidar || soloLectura;

  return (
    <div className="Dashboard-card" style={{ marginTop: "20px" }}>
      <div className="fila-encabezado">
        <h3 style={{ margin: 0, color: "#003b5c" }}>Historial de Pagos</h3>
      </div>
      <p style={{ margin: "8px 0 12px 0", fontSize: "0.88rem", color: "#555" }}>
        Arriba están las órdenes que falta pagar: si traen el ticket del pago, usa <b>Validar</b> en esa orden. Abajo están los pagos ya registrados.
        {ventanilla.length > 0 && (
          <> Los pagos de ventanilla se validan solos con el archivo del banco. Cargado hasta: {ventanilla.map((c) => `${c.fuente === "BBVA" ? "BBVA" : "Banorte"} ${ddmm(c.hasta)}`).join(" · ")}.</>
        )}
      </p>
      <AvisoSoloLectura visible={soloLectura} />

      {!cargando && textosAvisos.length > 0 && (
        <div role="status" style={{ margin: "0 0 12px 0", padding: "10px 12px", borderRadius: "6px", backgroundColor: "#e8f0fe", border: "1px solid #c5d6f7", color: "#003b5c", fontSize: "0.88rem" }}>
          <b>ℹ️ Cambios recientes en las órdenes de este alumno</b>
          <ul style={{ margin: "6px 0 0 0", paddingLeft: "20px" }}>
            {textosAvisos.map((t) => <li key={t} style={{ marginBottom: "4px" }}>{t}</li>)}
          </ul>
        </div>
      )}

      {cargando ? (
        <div style={{ padding: "40px", textAlign: "center", color: "#666" }}>
          <p><strong>Cargando historial de pagos...</strong></p>
        </div>
      ) : pendientes.length + pagos.length > 0 ? (
        <div style={{ overflowX: "auto", border: "1px solid #ddd", borderRadius: "8px" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
            <thead style={{ backgroundColor: "#f4f4f4", borderBottom: "2px solid #ddd" }}>
              <tr>
                <th className="celda-tabla celda-tabla-th">Fecha</th>
                <th className="celda-tabla celda-tabla-th">ID Transacción</th>
                <th className="celda-tabla celda-tabla-th">Banco</th>
                <th className="celda-tabla celda-tabla-th">Referencia / Folio</th>
                <th className="celda-tabla celda-tabla-th">Conceptos</th>
                {haySolicitante && <th className="celda-tabla celda-tabla-th">Solicitante</th>}
                <th className="celda-tabla celda-tabla-th">Método</th>
                <th style={{ padding: "12px 15px", fontSize: "0.9rem", color: "#333", textAlign: "right" }}>Importe</th>
                <th style={{ padding: "12px 15px", fontSize: "0.9rem", color: "#333", textAlign: "center" }}>Estatus</th>
              </tr>
            </thead>
            <tbody>
              {pendientes.map((ref) => {
                const esParcial = ref.estatus === "PARCIAL";
                const faltante = +(parseFloat(ref.importe_total || 0) - parseFloat(ref.abonado || 0)).toFixed(2);
                return (
                  <tr key={`p-${ref.id}`} style={{ borderBottom: "1px solid #eee", backgroundColor: esParcial ? "#fffdf5" : "#fffbea" }}>
                    <td className="celda-tabla">{ref.fecha_creacion}</td>
                    <td style={{ padding: "12px 15px", fontSize: "0.85rem" }}>
                      <span style={{ fontFamily: "monospace", color: "#555" }}>{ref.id}</span>
                      {ref.folio_tpv && <div style={{ fontSize: "0.75rem", color: "#777" }}>Folio {ref.folio_tpv}</div>}
                    </td>
                    <td className="celda-tabla" style={{ color: "#999" }}>—</td>
                    <td style={{ padding: "12px 15px", fontSize: "0.78rem", fontFamily: "monospace", color: "#555" }}>
                      {ref.referencia_banorte && <div>Banorte {ref.referencia_banorte}</div>}
                      {ref.referencia_bbva && <div>BBVA {ref.referencia_bbva}</div>}
                    </td>
                    <td className="celda-tabla">{conceptosDe(ref)}</td>
                    {haySolicitante && <td style={{ padding: "12px 15px", fontSize: "0.9rem", color: "#555" }}>{ref.exalumno || "—"}</td>}
                    <td className="celda-tabla" style={{ color: "#999" }}>—</td>
                    <td style={{ padding: "12px 15px", fontSize: "0.9rem", fontWeight: "bold", textAlign: "right" }}>
                      {dinero(ref.importe_total)}
                      {esParcial && <div style={{ fontSize: "0.78rem", color: "#d9534f" }}>Falta {dinero(faltante)}</div>}
                    </td>
                    <td style={{ padding: "12px 15px", textAlign: "center", minWidth: "170px" }}>
                      <span style={esParcial ? insignia("#ffe0b2", "#8a4b00") : insignia("#fff3cd", "#7a5c00")}>{esParcial ? "PARCIAL" : "PENDIENTE"}</span>
                      {verBotones && (esParcial ? (
                        <>
                          <button onClick={() => abrirModalComplemento(ref)} disabled={soloLectura || loadingValidacion}
                            title={soloLectura ? MENSAJE_SOLO_LECTURA : undefined} className="btn-select"
                            style={{ ...boton, backgroundColor: "#0056b3", color: "#fff", border: "none", borderRadius: "4px" }}>
                            {soloLectura ? "Cobrar faltante" : tienePinpad ? "Cobrar faltante con Pinpad" : "Cobrar faltante con TPV"}
                          </button>
                          <button onClick={() => abrirModalValidacion(ref)} disabled={soloLectura || loadingValidacion}
                            title={soloLectura ? MENSAJE_SOLO_LECTURA : undefined} className="btn-select validar" style={boton}>
                            Validar segundo voucher
                          </button>
                        </>
                      ) : (
                        <button onClick={() => abrirModalValidacion(ref)} disabled={soloLectura || loadingValidacion}
                          title={soloLectura ? MENSAJE_SOLO_LECTURA : "Registrar el pago de esta orden con el ticket del banco o de la terminal"}
                          className="btn-select validar" style={boton}>
                          Validar
                        </button>
                      ))}
                    </td>
                  </tr>
                );
              })}

              {pagos.map((pago, index) => {
                const parcialidad = infoParcialidad(pago);
                return (
                  <tr key={`h-${pago.id}-${index}`} style={{ borderBottom: "1px solid #eee", backgroundColor: parcialidad ? "#fff8e1" : (index % 2 === 0 ? "#fff" : "#fcfcfc") }}>
                    <td className="celda-tabla">{pago.fecha_pago}</td>
                    <td style={{ padding: "12px 15px", fontSize: "0.85rem" }}>
                      {parcialidad ? (
                        <span title={`Parcial ${parcialidad.numero} de este pago (folio ${parcialidad.folio})`}
                          style={{ fontFamily: "monospace", fontWeight: "bold", color: "#8a6d00", backgroundColor: "#ffe082", padding: "3px 8px", borderRadius: "10px" }}>
                          🔗 P{parcialidad.numero}-{parcialidad.folio}
                        </span>
                      ) : (
                        <span style={{ fontFamily: "monospace", color: "#555" }}>{pago.id}</span>
                      )}
                    </td>
                    <td style={{ padding: "12px 15px", fontSize: "0.9rem", fontWeight: "bold",
                      color: String(pago.banco).toUpperCase() === "BBVA" ? "#004481" : String(pago.banco).toUpperCase() === "BANORTE" ? "#eb0029" : "#00796b" }}>
                      {pago.banco}
                    </td>
                    <td style={{ padding: "12px 15px", fontSize: "0.85rem", fontFamily: "monospace", color: "#555" }}>{pago.folio_pago || pago.referencia}</td>
                    <td className="celda-tabla">{pago.conceptos}</td>
                    {haySolicitante && <td style={{ padding: "12px 15px", fontSize: "0.9rem", color: "#555" }}>{pago.exalumno || "—"}</td>}
                    <td className="celda-tabla">{pago.metodo_pago}</td>
                    <td style={{ padding: "12px 15px", fontSize: "0.9rem", fontWeight: "bold", textAlign: "right" }}>{dinero(pago.importe_total)}</td>
                    <td style={{ padding: "12px 15px", textAlign: "center" }}>
                      <span style={insignia("#d4edda", "#155724")}>{pago.estatus}</span>
                      {confirmadoPorBanco(pago) && <div style={{ fontSize: "0.72rem", color: "#137333", marginTop: "4px", whiteSpace: "nowrap" }}>✓ Confirmado por el banco</div>}
                      {validadoPorBanco(pago) && <div style={{ fontSize: "0.72rem", color: "#003b5c", marginTop: "2px" }}>Lo validó el sistema con el archivo del banco</div>}
                      {puedeReasignar && sePuedeReasignar(pago) && (
                        <div style={{ marginTop: "6px" }}>
                          <button onClick={() => setReasignando(pago)} className="btn-select"
                            title="Esta orden se validó con el pago de otro alumno"
                            style={{ border: "1px solid #003b5c", padding: "3px 8px", backgroundColor: "#fff", color: "#003b5c", fontSize: "0.75rem" }}>
                            Reasignar a otro alumno
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ padding: "40px", textAlign: "center", backgroundColor: "#f8f9fa", border: "1px dashed #ccc", borderRadius: "8px" }}>
          <h4 style={{ color: "#555", margin: "0 0 5px 0" }}>Sin órdenes ni pagos</h4>
          <p style={{ color: "#777", margin: 0, fontSize: "0.9rem" }}>No hay órdenes pendientes ni pagos registrados en el sistema.</p>
        </div>
      )}

      {reasignando && (
        <ReasignarPago pago={reasignando} onCerrar={() => setReasignando(null)} onReasignado={() => { setReasignando(null); onReasignado(); }} />
      )}

      <ModalValidarPago
        mostrar={mostrarModalValidacion && !!refSeleccionada}
        onClose={cerrarModal}
        onConfirm={confirmarValidacionPago}
        loading={loadingValidacion}
        datosPago={refSeleccionada ? {
          importe: refSeleccionada.importe_total,
          abonado: refSeleccionada.abonado,
          estatus: refSeleccionada.estatus,
          referenciaBanorte: refSeleccionada.referencia_banorte,
          referenciaBBVA: refSeleccionada.referencia_bbva,
          folioTPV: refSeleccionada.folio_tpv
        } : null}
        bancoSeleccionado={bancoSeleccionado}
        setBancoSeleccionado={setBancoSeleccionado}
        folioBanco={folioBanco}
        setFolioBanco={setFolioBanco}
        modoModal={modoModal}
        tienePinpad={tienePinpad}
        onCobrarPinpad={() => cobrarFaltantePinpad(refSeleccionada)}
        onCobrarTPV={(folio) => cobrarFaltanteTPV(refSeleccionada, folio)}
        faltante={calcularFaltante ? calcularFaltante(refSeleccionada) : 0}
      />
    </div>
  );
};

export default VistaHistorialDePagos;
